import { useCallback, useRef } from "react";
import { getLoadingMessage } from "src/constants/loadingMessages";
import { beginGlobalLoading, endGlobalLoading } from "src/slices/settings";
import { useDispatch } from "src/store";

const removeSourceFromStack = (stack, source) => {
  const idx = stack.lastIndexOf(source);
  if (idx >= 0) {
    stack.splice(idx, 1);
  }
};

const useGlobalLoading = (defaultSource = "global") => {
  const dispatch = useDispatch();
  const stackRef = useRef([]);

  const begin = useCallback(
    ({ source, message, messageKey, metadata } = {}) => {
      const resolvedSource = source || defaultSource;
      const resolvedMessage = message || getLoadingMessage(messageKey);

      stackRef.current.push(resolvedSource);
      dispatch(
        beginGlobalLoading({
          source: resolvedSource,
          message: resolvedMessage,
          metadata,
        }),
      );

      return resolvedSource;
    },
    [defaultSource, dispatch],
  );

  const end = useCallback(
    ({ source, metadata } = {}) => {
      const resolvedSource = source || stackRef.current.pop() || defaultSource;
      if (source) {
        removeSourceFromStack(stackRef.current, source);
      }

      dispatch(
        endGlobalLoading({
          source: resolvedSource,
          metadata,
        }),
      );
    },
    [defaultSource, dispatch],
  );

  const withLoading = useCallback(
    async (task, options = {}) => {
      const source = begin(options);
      try {
        return await task();
      } finally {
        end({ source, metadata: options.metadata });
      }
    },
    [begin, end],
  );

  return {
    begin,
    end,
    withLoading,
    getMessage: getLoadingMessage,
  };
};

export default useGlobalLoading;
